/**
 * Esquemas Zod de la frontera HTTP (SDD SaaS §8.3). La API valida con ellos;
 * la web y la extensión los usan para tipar las peticiones.
 */
import { z } from 'zod'
import { ABSOLUTE_MAX_RECORDING_MIN } from './plans'

/** Trozos de 10 s: 8 h = 2.880 trozos por pista (SDD §8.3). */
export const MAX_CHUNK_SEQ = (ABSOLUTE_MAX_RECORDING_MIN * 60) / 10
/** Tope de bytes por trozo firmado en la URL prefirmada. */
export const MAX_CHUNK_BYTES = 2 * 1024 * 1024
/** Trozos por lote de presign. */
export const MAX_PRESIGN_BATCH = 50

export const trackLabelSchema = z.enum(['mic', 'others', 'single'])
export const recordingModeSchema = z.enum(['online', 'presencial'])
export const platformSchema = z.enum(['meet', 'teams', 'zoom', 'otro'])
export const templateIdSchema = z.enum(['reunion_general', 'entrevista', 'reunion_cliente'])
export const planIdSchema = z.enum(['free', 'personal', 'equipo'])

const uuid = z.string().uuid()
const titulo = z.string().trim().min(1).max(200)
/** Idioma en que se habla la reunión (las notas siempre se escriben en español). */
export const languageSchema = z.enum(['es', 'es-419', 'en', 'pt'])
export type MeetingLanguage = z.infer<typeof languageSchema>

/** Nombres para los selectores. */
export const LANGUAGE_LABELS: Record<'es' | 'en' | 'pt', string> = {
  es: 'Español',
  en: 'Inglés',
  pt: 'Portugués'
}

export const createRecordingSchema = z.object({
  /** La extensión puede proponer su propio id para poder grabar sin red (SDD §6.4). */
  id: uuid.optional(),
  modo: recordingModeSchema,
  origen: z.literal('extension'),
  plataforma: platformSchema.nullable().default(null),
  projectId: uuid.nullable().default(null),
  titulo: titulo.default('Reunión sin título'),
  idioma: languageSchema.default('es'),
  consentimientoDeclarado: z.boolean().default(false),
  fechaInicio: z.string().datetime().optional()
})
export type CreateRecordingInput = z.infer<typeof createRecordingSchema>

export const presignChunksSchema = z.object({
  chunks: z
    .array(
      z.object({
        track: trackLabelSchema,
        seq: z
          .number()
          .int()
          .min(0)
          .max(MAX_CHUNK_SEQ - 1),
        bytes: z.number().int().min(1).max(MAX_CHUNK_BYTES)
      })
    )
    .min(1)
    .max(MAX_PRESIGN_BATCH)
})
export type PresignChunksInput = z.infer<typeof presignChunksSchema>

export const ackChunksSchema = z.object({
  track: trackLabelSchema,
  seqs: z
    .array(
      z
        .number()
        .int()
        .min(0)
        .max(MAX_CHUNK_SEQ - 1)
    )
    .min(1)
    .max(500)
})
export type AckChunksInput = z.infer<typeof ackChunksSchema>

/**
 * Línea de tiempo de voz del micrófono: pares [inicioMs, finMs] donde hubo voz,
 * calculada en la extensión con un `AnalyserNode` (SDD §6.2). Unos KB por hora.
 */
export const vadTimelineSchema = z
  .array(z.tuple([z.number().int().min(0), z.number().int().min(0)]))
  .max(20_000)

export const completeRecordingSchema = z.object({
  tracks: z
    .array(
      z.object({
        label: trackLabelSchema,
        t0Ms: z.number().int().min(0),
        duracionMs: z
          .number()
          .int()
          .min(0)
          .max(ABSOLUTE_MAX_RECORDING_MIN * 60_000),
        trozosEsperados: z.number().int().min(0).max(MAX_CHUNK_SEQ)
      })
    )
    .min(1)
    .max(2),
  vadMic: vadTimelineSchema.optional(),
  titulo: titulo.optional(),
  advertencias: z.array(z.string().max(40)).max(20).optional(),
  /** Momentos marcados con Alt+Shift+M (ms desde el inicio). */
  marcas: z
    .array(
      z
        .number()
        .int()
        .min(0)
        .max(ABSOLUTE_MAX_RECORDING_MIN * 60_000)
    )
    .max(200)
    .optional()
})
export type CompleteRecordingInput = z.infer<typeof completeRecordingSchema>

/** Tipos MIME aceptados en importación; la API valida además los magic bytes. */
export const IMPORT_MIME_TYPES = [
  'audio/webm',
  'audio/ogg',
  'audio/mpeg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'video/webm',
  'video/mp4'
] as const

export const importPresignSchema = z.object({
  nombre: z.string().trim().min(1).max(200),
  bytes: z.number().int().min(1),
  tipo: z.enum(IMPORT_MIME_TYPES)
})
export type ImportPresignInput = z.infer<typeof importPresignSchema>

export const importCompleteSchema = z.object({
  recordingId: uuid,
  titulo: titulo.optional(),
  projectId: uuid.nullable().default(null),
  idioma: languageSchema.default('es'),
  fechaInicio: z.string().datetime().optional()
})
export type ImportCompleteInput = z.infer<typeof importCompleteSchema>

export const patchRecordingSchema = z
  .object({
    titulo: titulo.optional(),
    descripcion: z.string().trim().max(2000).nullable().optional(),
    projectId: uuid.nullable().optional(),
    consentimientoDeclarado: z.boolean().optional(),
    visibilidad: z.enum(['privada', 'equipo']).optional()
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nada que actualizar' })

export const renameSpeakerSchema = z.object({
  nombre: z.string().trim().max(80).nullable()
})

export const mergeSpeakersSchema = z.object({
  /** Ids de hablantes que se fusionan en `targetId`. */
  sourceIds: z.array(z.number().int().positive()).min(1).max(20),
  targetId: z.number().int().positive()
})

export const createSummarySchema = z
  .object({
    templateId: z.union([templateIdSchema, z.literal('personalizada')]),
    customTemplateId: uuid.optional()
  })
  .refine((v) => v.templateId !== 'personalizada' || !!v.customTemplateId, {
    message: 'Falta la plantilla personalizada',
    path: ['customTemplateId']
  })

const customSectionSchema = z.object({
  titulo: z.string().trim().min(1).max(60),
  instruccion: z.string().trim().max(300).default('')
})

/** Crear o reemplazar una plantilla personalizada. */
export const customTemplateSchema = z.object({
  nombre: z.string().trim().min(1).max(60),
  descripcion: z.string().trim().max(200).default(''),
  secciones: z.array(customSectionSchema).min(1).max(8)
})
export type CustomTemplateInput = z.infer<typeof customTemplateSchema>

/** Sin repetir sin importar mayúsculas; gana la primera forma escrita. */
export function uniqueCaseInsensitive(xs: string[]): string[] {
  const vistos = new Set<string>()
  return xs.filter((x) => {
    const k = x.toLowerCase()
    if (vistos.has(k)) return false
    vistos.add(k)
    return true
  })
}

/** Vocabulario de la organización: hasta 100 términos de 2 a 50 caracteres (frases de ≤ 6 palabras). */
export const vocabularySchema = z.object({
  terminos: z
    .array(
      z
        .string()
        .transform((t) =>
          t
            // eslint-disable-next-line no-control-regex
            .replace(/[\u0000-\u001f]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
        )
        .pipe(
          z
            .string()
            .min(2)
            .max(50)
            .refine((t) => t.split(' ').length <= 6, { message: 'Máximo 6 palabras por término' })
        )
    )
    .max(100)
    .transform((ts) => uniqueCaseInsensitive(ts))
})

/** Ajustes de privacidad de la organización (retención y links públicos). */
export const RETENCION_OPCIONES = [30, 90, 180, 365, 730] as const
export const orgSettingsSchema = z
  .object({
    /** Días que se conservan las reuniones; null = sin borrado automático. */
    retencionDias: z
      .union([z.literal(30), z.literal(90), z.literal(180), z.literal(365), z.literal(730)])
      .nullable()
      .optional(),
    compartirHabilitado: z.boolean().optional()
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nada que actualizar' })
export type OrgSettingsInput = z.infer<typeof orgSettingsSchema>

/** Tema a seguir: nombre y hasta 10 palabras o frases cortas. */
export const topicTrackerSchema = z.object({
  nombre: z.string().trim().min(1).max(40),
  terminos: z
    .array(
      z
        .string()
        // Sin signos: en la búsqueda «-x» sería una negación y comillas cambiarían la consulta
        .transform((t) =>
          t
            .replace(/[^\p{L}\p{N} ]+/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
        )
        .pipe(
          z
            .string()
            .min(2)
            .max(40)
            .refine((t) => t !== 'or', { message: '«or» no sirve como término' })
        )
    )
    .min(1)
    .max(10)
    .transform((ts) => [...new Set(ts)])
})
export type TopicTrackerInput = z.infer<typeof topicTrackerSchema>

/** Enviar las notas por correo a los participantes. */
export const sendNotesSchema = z.object({
  destinatarios: z
    .array(z.string().trim().toLowerCase().email().max(254))
    .min(1)
    .max(10)
    .transform((xs) => [...new Set(xs)]),
  mensaje: z.string().trim().max(500).optional()
})
export type SendNotesInput = z.infer<typeof sendNotesSchema>

/** Alta de un webhook saliente (la URL se valida además contra destinos internos). */
export const createWebhookSchema = z.object({
  url: z.string().trim().url().max(500),
  incluirTranscripcion: z.boolean().default(false)
})
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>

export const editSummarySchema = z.object({
  contenido: z.unknown()
})

export const summaryFeedbackSchema = z.object({
  feedback: z.enum(['up', 'down']).nullable(),
  motivo: z.string().trim().max(500).optional()
})

export const createProjectSchema = z.object({
  nombre: z.string().trim().min(1).max(120),
  descripcion: z.string().trim().max(2000).nullable().default(null)
})

export const patchProjectSchema = z
  .object({
    nombre: z.string().trim().min(1).max(120).optional(),
    descripcion: z.string().trim().max(2000).nullable().optional(),
    /** Carpeta del equipo: todos los miembros ven sus reuniones. */
    compartido: z.boolean().optional()
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nada que actualizar' })

// Plan Equipo
export const createTeamSchema = z.object({ nombre: z.string().trim().min(2).max(80) })
export const inviteSchema = z.object({
  email: z.string().trim().email().max(200),
  rol: z.enum(['admin', 'member']).default('member')
})
export const memberRoleSchema = z.object({ rol: z.enum(['admin', 'member']) })
export const seatsSchema = z.object({ asientos: z.number().int().min(2).max(200) })
export const acceptInvitationSchema = z.object({ token: z.string().min(20).max(100) })

/** Turnos anteriores de la conversación (solo contexto; no se guardan). */
const askHistorySchema = z
  .array(
    z.object({
      pregunta: z.string().trim().max(500),
      respuesta: z.string().trim().max(1500)
    })
  )
  .max(3)
  .default([])

export const askSchema = z.object({
  pregunta: z.string().trim().min(3).max(500),
  historial: askHistorySchema
})
export type AskTurn = { pregunta: string; respuesta: string }

/**
 * Corrección de notas por chat: lo que el usuario dice que está mal y, como
 * contexto, sus correcciones anteriores con la respuesta que recibió.
 */
export const correctSummarySchema = z.object({
  instruccion: z.string().trim().min(3).max(1000),
  historial: z
    .array(
      z.object({
        instruccion: z.string().trim().max(1000),
        respuesta: z.string().trim().max(500)
      })
    )
    .max(5)
    .default([])
})
export type CorrectionTurn = { instruccion: string; respuesta: string }

/** Pregunta a todas las reuniones (o a las de una carpeta). */
export const askAcrossSchema = askSchema.extend({
  projectId: z.string().uuid().optional()
})

export const createShareSchema = z.object({
  alcance: z.enum(['notas', 'notas_transcripcion']).default('notas'),
  ocultarNombres: z.boolean().default(false),
  /** Días de vigencia; null = sin vencimiento. */
  expiraEnDias: z
    .union([z.literal(7), z.literal(30), z.literal(90)])
    .nullable()
    .default(30)
})

export const searchQuerySchema = z.object({
  // Hasta 500: un tema con varias palabras se busca como «a or b or c…»
  q: z.string().trim().min(1).max(500),
  projectId: uuid.optional(),
  /** Solo reuniones de los últimos N días. */
  dias: z.coerce.number().int().min(1).max(3650).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20)
})

export const listRecordingsSchema = z.object({
  projectId: uuid.optional(),
  estado: z.enum(['activo', 'retenida', 'todos']).default('todos'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(50)
})

export const patchMeSchema = z
  .object({
    nombre: z.string().trim().max(120).nullable().optional(),
    locale: z
      .string()
      .regex(/^[a-z]{2}(-[A-Z]{2})?$/)
      .optional(),
    aceptarConsentimiento: z.literal(true).optional(),
    aceptarTerminos: z.literal(true).optional(),
    notificaciones: z
      .object({
        notas_listas: z.boolean().optional(),
        cuota: z.boolean().optional(),
        resumen_semanal: z.boolean().optional(),
        consejos: z.boolean().optional()
      })
      .optional(),
    plantillaDefecto: templateIdSchema.optional()
  })
  .strict()
export type PatchMeInput = z.infer<typeof patchMeSchema>

/**
 * De dónde llegó quien se registra (utm_* del anuncio o del post, gclid/fbclid, o el
 * sitio que lo trajo). Lo manda la web una sola vez tras el registro; sin cookies.
 */
const campoOrigen = z.string().trim().min(1).max(200).optional()
export const origenSchema = z
  .object({
    fuente: campoOrigen,
    medio: campoOrigen,
    campana: campoOrigen,
    contenido: campoOrigen,
    termino: campoOrigen,
    gclid: campoOrigen,
    fbclid: campoOrigen,
    ref: campoOrigen,
    landing: campoOrigen
  })
  .strict()
export type Origen = z.infer<typeof origenSchema>

export const checkoutSchema = z.object({
  plan: z.enum(['personal', 'equipo']),
  /** Solo Equipo: asientos (mínimo 2). */
  asientos: z.number().int().min(2).max(200).optional(),
  /** Desde dónde se abrió el pago (analítica del embudo). */
  disparador: z.enum(['plan', 'cuota', 'retenida', 'bienvenida']).optional()
})

/** Eventos de analítica que la web o la extensión pueden reportar (SDD §15.5). */
export const clientEventSchema = z.object({
  evento: z.enum(['extension_instalada', 'mic_permitido', 'notas_vistas', 'deteccion_meet']),
  props: z
    .record(
      z.string().regex(/^[a-z_]{1,40}$/),
      z.union([z.string().max(32), z.number(), z.boolean(), z.null()])
    )
    .optional()
})
export type ClientEvent = z.infer<typeof clientEventSchema>

export const extensionLinkSchema = z.object({})

/** Cabecera `X-Client`: `extension/1.4.2` o `web/1.4.2`. */
export const clientHeaderSchema = z.string().regex(/^(extension|web)\/\d+\.\d+\.\d+$/)

// ---------------------------------------------------------------------------
// Panel de administración (docs/panel-admin.md)
// ---------------------------------------------------------------------------
const enteroQuery = (min: number, max: number) =>
  z.coerce.number().int().min(min).max(max).optional()

export const adminFiltroUsuariosSchema = z.object({
  q: z.string().trim().max(200).optional(),
  plan: z.enum(['free', 'personal', 'equipo']).optional(),
  estado: z.enum(['sin_activar', 'activo', 'inactivo', 'suspendido']).optional(),
  orden: z.enum(['recientes', 'actividad', 'reuniones', 'correo']).optional(),
  limite: enteroQuery(1, 200),
  desplazamiento: enteroQuery(0, 1_000_000)
})

export const adminAuditoriaQuerySchema = z.object({
  accion: z.string().trim().max(60).optional(),
  operadorId: z.string().uuid().optional(),
  orgId: z.string().uuid().optional(),
  limite: enteroQuery(1, 200),
  desplazamiento: enteroQuery(0, 1_000_000)
})

const motivo = z.string().trim().min(3, 'Escribe el motivo.').max(500)

export const adminMotivoSchema = z.object({ motivo })
export const adminCortesiaSchema = z.object({ dias: z.number().int().min(1).max(365), motivo })
export const adminNotaSchema = z.object({ texto: z.string().trim().min(1).max(2000) })
export const adminBorrarCuentaSchema = z.object({
  confirmacion: z.string().trim().min(3).max(200),
  motivo
})
export const adminDescuentoSchema = z.object({
  codigo: z
    .string()
    .trim()
    .regex(/^[a-zA-Z0-9]{3,32}$/, 'Solo letras y números, de 3 a 32.'),
  descripcion: z.string().trim().min(3).max(200),
  porcentaje: z.number().min(1).max(100),
  meses: z.number().int().min(1).max(36).nullable(),
  maxUsos: z.number().int().min(1).max(100_000).nullable(),
  expiraEn: z.string().datetime({ offset: true }).nullable()
})
